import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

class DeepSweepCartWarranty extends HTMLElement {
  #busy = false;

  connectedCallback() {
    this.input?.addEventListener('change', this.#handleChange);

    const regularItemCount = Number(this.dataset.regularItemCount || 0);
    const lineQuantity = Number(this.dataset.lineQuantity || 0);

    if (regularItemCount === 0 && this.dataset.lineKey) {
      this.#update(false, true);
    } else if (lineQuantity > 1) {
      this.#setQuantity(1, true);
    }
  }

  disconnectedCallback() {
    this.input?.removeEventListener('change', this.#handleChange);
  }

  get input() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('input[type="checkbox"]'));
  }

  get status() {
    return this.querySelector('[aria-live]');
  }

  #handleChange = () => {
    this.#update(Boolean(this.input?.checked), false);
  };

  async #update(enable, cleanup) {
    if (this.#busy) return;

    if (enable) {
      await this.#add();
      return;
    }

    await this.#setQuantity(0, cleanup);
  }

  async #add() {
    const variantId = Number(this.dataset.variantId);
    if (!variantId) return this.#fail(new Error('Warranty variant is unavailable'), false);

    const sections = this.#sectionIds();
    const body = {
      items: [{ id: variantId, quantity: 1, properties: { _ds_warranty: 'true' } }],
      sections: sections.join(','),
      sections_url: window.location.pathname,
    };

    await this.#mutate({
      action: 'add',
      enabledAfterFailure: false,
      request: fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }),
    });
  }

  async #setQuantity(quantity, cleanup) {
    const lineKey = this.dataset.lineKey;
    if (!lineKey) {
      if (!quantity) this.#setChecked(false);
      return;
    }

    const sections = this.#sectionIds();
    const body = {
      id: lineKey,
      quantity,
      sections: sections.join(','),
      sections_url: window.location.pathname,
    };

    await this.#mutate({
      action: quantity > 0 ? 'update' : 'remove',
      enabledAfterFailure: true,
      silent: cleanup,
      request: fetch(Theme.routes.cart_change_url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }),
    });
  }

  async #mutate({ action, request, enabledAfterFailure, silent = false }) {
    this.#setBusy(true);
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action,
        context: 'cart',
        lines: [{ merchandiseId: this.dataset.variantId || '', quantity: action === 'remove' ? 0 : 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await request;
      const data = await response.json();
      if (!response.ok || data.status || data.errors) {
        throw new Error(data.description || data.message || data.errors || 'Unable to update warranty');
      }

      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!cartResponse.ok) throw new Error('Unable to refresh cart');
      const cart = await cartResponse.json();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          sections: data.sections,
          items: cart.items,
          itemCount: cart.item_count,
          source: 'deepsweep-cart-warranty',
          didError: false,
        },
      });

      if (!silent) this.#announce(action === 'add' ? 'Lifetime warranty added.' : 'Lifetime warranty removed.');
    } catch (error) {
      deferred.reject(error);
      this.#setChecked(enabledAfterFailure);
      this.#fail(error, enabledAfterFailure);
    } finally {
      this.#setBusy(false);
    }
  }

  #sectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((component) => component.dataset.sectionId)
      .filter(Boolean);
  }

  #setBusy(busy) {
    this.#busy = busy;
    this.toggleAttribute('aria-busy', busy);
    if (this.input) this.input.disabled = busy;
  }

  #setChecked(checked) {
    if (this.input) this.input.checked = checked;
  }

  #announce(message) {
    if (this.status) this.status.textContent = message;
  }

  #fail(error, checked) {
    const message = error instanceof Error ? error.message : 'Unable to update warranty';
    this.#setChecked(checked);
    this.#announce(message);
    this.dispatchEvent(new CartErrorEvent({ error: message, code: 'SERVICE_UNAVAILABLE' }));
  }
}

if (!customElements.get('ds-cart-warranty')) {
  customElements.define('ds-cart-warranty', DeepSweepCartWarranty);
}
